import React from 'react';
import { Grid3x3 } from 'lucide-react';

/**
 * VendorHeroGallery Component
 * Displays vendor portfolio images in a hero gallery layout.
 * This component is specifically designed to show only portfolio images (image_type='portfolio').
 * Returns null if no portfolio images are provided.
 */
const VendorHeroGallery = ({ images = [], vendorName = '', onShowAll }) => {
  // If no portfolio images, return null (blank)
  if (!images || images.length === 0) {
    return null;
  }

  // Use provided portfolio images
  const displayImages = images;

  const showAllPhotos = () => {
    if (onShowAll) {
      onShowAll();
    }
  };

  const singleImage = displayImages.length === 1;

  return (
    <div className="relative h-[300px] w-full overflow-hidden rounded-2xl sm:h-[400px] md:h-[500px]">
      <div className={`grid h-full ${singleImage ? 'grid-cols-1' : 'grid-cols-4 gap-1.5'}`}>
        {/* Main Large Image — full width when alone, otherwise left half on md+ */}
        <div
          className={`relative flex h-full items-center justify-center overflow-hidden bg-m-border-light animate-pulse ${
            singleImage ? 'col-span-1 w-full' : 'col-span-4 md:col-span-2'
          }`}
        >
          <img
            src={displayImages[0]}
            alt={vendorName}
            className="h-full min-h-full w-full min-w-full object-cover object-center"
            loading="eager"
            onLoad={(e) => {
              e.target.parentElement.classList.remove('animate-pulse', 'bg-m-border-light');
            }}
            onError={(e) => {
              e.target.parentElement.classList.remove('animate-pulse');
              e.target.parentElement.classList.add('bg-m-canvas-light');
            }}
          />
        </div>

        {/* Thumbnail Grid - Right Side (4 images in 2x2); hidden when only one photo */}
        {!singleImage && (
        <div className="hidden md:grid md:col-span-2 grid-cols-2 gap-1.5 h-full">
          {displayImages.slice(1, 5).map((image, index) => {
            // Only add border to corners: index 0 (top-right) and index 3 (bottom-right)
            const isCorner = index === 0 || index === 3;
            return (
              <div
                key={index}
                className={`relative flex h-full items-center justify-center overflow-hidden bg-m-border-light animate-pulse ${
                  isCorner ? '' : ''
                }`}
              >
                <img
                  src={image}
                  alt={`${vendorName} - ${index + 2}`}
                  className="h-full min-h-full w-full min-w-full object-cover object-center"
                  loading="lazy"
                  onLoad={(e) => {
                    e.target.parentElement.classList.remove('animate-pulse', 'bg-m-border-light');
                  }}
                  onError={(e) => {
                    e.target.parentElement.classList.remove('animate-pulse');
                    e.target.parentElement.classList.add('bg-m-canvas-light');
                  }}
                />
                {index === 3 && displayImages.length > 5 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      showAllPhotos();
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 font-medium text-white transition-colors hover:bg-black/60"
                  >
                    <div className="text-center">
                      <Grid3x3 className="mx-auto mb-1 h-6 w-6" />
                      <span className="text-sm">
                        Show all {displayImages.length} photos
                      </span>
                    </div>
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Mobile: Show all photos button */}
      {displayImages.length > 1 ? (
        <button
          type="button"
          onClick={showAllPhotos}
          className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-m-surface px-4 py-2 text-sm font-medium text-m-ink shadow-sm transition-colors hover:bg-m-canvas-light md:hidden"
        >
          <Grid3x3 className="h-4 w-4" />
          <span>Show all {displayImages.length} photos</span>
        </button>
      ) : null}
    </div>
  );
};

export default VendorHeroGallery;
